
import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "./kStyles";
import { kObject, kObjectArray } from "../ReUsableTS/kBrowserObjects";
import {kAnimTextObj} from "../ReUsableTS/kAnimateTexts";
import { kHTMLRow, kHTMLTable } from "./kHTML";

// kSearch is the BROWSER UI layer. All string matching, ranking and highlight-string
// generation live in kSearchItemCore / kSearchItemsCore (kReusableCoreTS/kSearchCore.ts)
// — accent-folded matching, Search / SearchAllWords, GetHighlightedText(MultiWord),
// NonHidden, and the static people-name matchers NameMatches / NameScore. Those core
// classes sit BELOW kObject / kObjectArray in the chain, so the two classes below extend
// kObject / kObjectArray and still inherit the whole engine. Add only browser-specific
// pieces here: the .highlight stylesheet (kSearchItems.AddStyles) and kSearchInput.
//
// The parents matter. kSearchItem MUST extend kObject and kSearchItems MUST extend
// kObjectArray - a 2026 refactor hung them straight off the core classes instead, which
// stripped ToRow / ToTable / CallFunctionOnEach off every search-backed collection and
// broke consumers that pass one where a kObjectArray is expected. Chain diagram and the
// full account: top of kReusableCoreTS/kSearchCore.ts.

// kSearchItem is a thin browser marker — every field and behaviour (SearchableString
// fold, FilteredOut, CheckMatch) is inherited from kSearchItemCore via kObject.
export class kSearchItem extends kObject {
}

export class kSearchItems extends kObjectArray {
    constructor(){
        super();
        this.AddStyles();
    }

    // DOM-only: inject the shared .highlight stylesheet the highlighter output relies on.
    // Kept in the browser layer because kSearchItemsCore must stay free of document/DOM.
    public AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const ExistingStyle = document.getElementById("kSearchStyles");
            if (ExistingStyle === null) {
                const Css: string = `

                .highlight {
                    background-color: yellow;
                }
                `
                kStyles.AddStyles("kSearchStyles", Css);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSearchItems.AddStyles");
            return;
        }
    }

    /** Lazy shared-highlighter shell for both picker-side and table-side hosts: return
     *  Current when already built, otherwise build one kSearchItems (its constructor
     *  injects the .highlight stylesheet the wrapped output relies on). The guard and
     *  catch paths return null - kEditableTable's historical contract; the people-picker
     *  host's single caller null-checks exactly like kEditableTable's does.
     *  The KGlobalErrorHandler label is always the caller's own. */
    public static EnsureHighlighter(Current: kSearchItems | null, Label: string): kSearchItems | null {
        // ERROR-SUPPRESSED-JUSTIFIED: shared helper - the catch reports under the CALLER's label (Label),
        // which a literal "kSearchItems.EnsureHighlighter" would replace with the wrong name.
        if (ks.KErrorMode === true) { return null; }
        try {
            if (Current === null) { return new kSearchItems(); }
            return Current;
        } catch (e) {
            ks.KGlobalErrorHandler(e, Label);
            return null;
        }
    }
}

export class kSearchInput{
    public Input:HTMLInputElement = document.createElement("input");
    public OuterDiv:HTMLDivElement = document.createElement("div");
    public AutoCompleteCell:HTMLTableCellElement = document.createElement("td");
    public SearchChangedCB:((Term:string) => void) | null = null;
    public _PrevSearchTerm:string = "";
    public CurrentSearchTerm:string = "";
    public TypingTimeOutMS:number = 100;
    public LastChange:number = 0; 
    public TypingTimer:ReturnType<typeof setTimeout> | null = null;
    public MatchesCountDisplay:kAnimTextObj = new kAnimTextObj();
    constructor(){
        this.AddStyles()
        this.Input.type = "search";
        this.Input.setAttribute("incremental","true");
        this.Input.setAttribute("spellcheck","true");
        this.Input.className ="kSearchInput";
        this.Input.addEventListener("keyup",this._SearchChanged);
        this.Input.addEventListener("search",this._SearchChanged);
        this.BuildSearchTable();
        
    }
    public BuildSearchTable = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const SearchTable:kHTMLTable = new kHTMLTable();
            SearchTable.Table.style.width = "100%";
            const SearchRow:kHTMLRow = SearchTable.AddEmptyRow();
            const SearchCell:HTMLTableCellElement = SearchRow.AddCellWithElement(this.Input);
            SearchCell.style.width = "100%";
            const MatchesCell:HTMLTableCellElement = SearchRow.AddCell();
            MatchesCell.style.cssFloat = "right";
            this.MatchesCountDisplay.Object = MatchesCell;
            const AutoCompleteRow:kHTMLRow = SearchTable.AddEmptyRow();
            this.AutoCompleteCell = AutoCompleteRow.AddMergedCell(2);
            this.OuterDiv.appendChild(SearchTable.Table);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSearchInput.BuildSearchTable");
            return;
        }
    }
    public AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const ExistingStyle = document.getElementById("kSearchInput");
            if (ExistingStyle === null) {
                const Css: string = `
                        .kSearchInput{
                            background: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAFiUAABYlAUlSJPAAAAGHaVRYdFhNTDpjb20uYWRvYmUueG1wAAAAAAA8P3hwYWNrZXQgYmVnaW49J++7vycgaWQ9J1c1TTBNcENlaGlIenJlU3pOVGN6a2M5ZCc/Pg0KPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyI+PHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj48cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0idXVpZDpmYWY1YmRkNS1iYTNkLTExZGEtYWQzMS1kMzNkNzUxODJmMWIiIHhtbG5zOnRpZmY9Imh0dHA6Ly9ucy5hZG9iZS5jb20vdGlmZi8xLjAvIj48dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPjwvcmRmOkRlc2NyaXB0aW9uPjwvcmRmOlJERj48L3g6eG1wbWV0YT4NCjw/eHBhY2tldCBlbmQ9J3cnPz4slJgLAAADoUlEQVRYR8WWPUhybRyHfx6yp0ghCqOChAYrKIiGkgqCQCMlmmor6EsaWgua26KloGgoi4IaGiIawtA+caqtkiAxiBwioiRNBcvfO7y+B8+tPeoLPc8FZ/Bcf2+uc6vHoyJJ/EUk8cSf5q8HqLL9CC4uLnB4eIirqyu8vr5CkiTodDoYjUZYLBbU1NSIb8kOZsDpdLKjo4MAvj0KCgrY399Pj8cjvj0jv92B6elpzM7Oyq9bWlrQ3NyM6upqxGIxeL1euN1u3N3dAQC0Wi0WFhYwPDyctEoGxKL/GBsbk6/QbDbz7OxMHCFJhsNhbm9vs7a2Vp5fXl4Wx74lbcDc3Jy82NTUFOPxuDiSwsvLC3t6egiAkiTx+PhYHElLSsDd3R0LCwsJgOPj46L+LZFIhK2trQTA+vp6RqNRcSSFlICJiQkCoMFgYCQSEXVGPB6PfAGbm5uiTkER8P7+zoqKCgLg0tJSssqJwcFBAqDFYhFVCoqAo6MjAqBGo+HDw0Oyyom9vT0CYHl5Od/e3kStQHEn9Pl8AICqqipUVlYmq5yoq6uDJEl4enqC3+8XtQJFQCQSAQBoNBrk5eUlq5zQarUoKioCAESjUVErUAT8+vULSITE4/FklRPhcBjhcBgAoFarRa1AEaDX6wEAfr8fz8/PySon7u/v8fX1hdLS0owfpSLAaDSipKQEgUAALpcrWeXE/v4+AKChoQE6nU7USsRv5cDAAAGwqalJVFnx+PjI4uJiAuD8/LyoU0gJuLy8pCRJBMCZmRlRZ6S3t5cAqNfrGQgERJ1CSgBJTk5Oyv8Fi4uLok7L19cXbTYbAVCtVvPg4EAcSUvagGg0SqvVKkcMDQ3R6/WKYzJut5udnZ3yfH5+Pjc2NsSxtHz7PBCJRGCz2bC1tQUk7g0mkwnt7e3Q6/WIxWLw+Xw4OTnB6ekpkPgZGwwG3NzcQKVSYWVlBaOjo8LKAmKRyOrqKmtqauSr++5oa2vj+fk5Q6EQu7u75fN2u11cUsG3O5BMMBiEw+GA0+nE9fU1AoEAJElCWVkZGhsbYbVaYTaboVKpAAAfHx/o6+uDw+EAANjtdoyMjAirJhCLsiEWi/Hz81M8rSAUCrGrqyvjTvyvgGzJJuJHA0gyGAzSbDbLEWtrawr/4wFMPOgk78T6+rrs/kgAEzthMpnkiJ2dHVJ8IPlJNBoNdnd3YTKZAAC3t7f/CrH0pwmFQnS5XPz4+CCzvQ/8JP8AfW9zaP6rjSUAAAAASUVORK5CYII=');
                            background-size: contain;
                            background-repeat: no-repeat;
                            background-position-x: 5px;

                            padding-left: 30px;
                            border: thin solid grey;
                            border-radius: 10px;
                            width:calc(100% - 30px)!Important;
                        }
                    `;
                kStyles.AddStyles("kSearchInput", Css);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSearchInput.AddStyles");
            return;
        }

    }
    private _SearchChanged = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if(this.TypingTimer !== null){
                clearTimeout(this.TypingTimer);
            }
            this.TypingTimer = setTimeout(this._TypingDone,this.TypingTimeOutMS);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSearchInput._SearchChanged");
            return;
        }
    }
    private _TypingDone = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.CurrentSearchTerm = this.Input.value.toLowerCase();
            if(this.CurrentSearchTerm !== this._PrevSearchTerm){
                ks.DoCallBack(this.SearchChangedCB,this.CurrentSearchTerm);
                this._PrevSearchTerm = this.CurrentSearchTerm;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSearchInput._TypingDone");
            return;
        }
    }
}


